import React, { useRef, useState } from 'react';
import { H1, Button, colors, Icon } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { useGraphQLQuery } from '@tviklax/ui-components';
import { useUser } from '../../../context/UserContext';
import { DashboardContainer } from './styles';

const CUSTOMER_DASHBOARD_QUERY = gql`
  query CustomerDashboard($customerId: Int, $contactId: Int!) {
    myOrders(customerId: $customerId, limit: 100, offset: 0) {
      orderId
      status
      amount
      createdAt
      trackingNumber
      carrier
      counterpartyName
      counterpartyEmail
      vendorId
      shipmentNo
      estimatedArrivalDate
    }
    myTradePartnerContacts(limit: 100) {
      items {
        id
        name
        email
        partnerId
      }
    }
    myPayments(customerId: $customerId, limit: 50, offset: 0) {
      paymentId
      orderId
      amount
      amountPaid
      status
      paymentMethod
    }
    myDocuments(customerId: $customerId, limit: 50, offset: 0) {
      documentId
      orderId
      documentName
      status
      dueDate
      impact
      owner
    }
    messageThreads(contactId: $contactId, limit: 20, offset: 0) {
      id
      subject
      relatedOrderId
      lastMessageAt
      unreadCount
    }
    unreadMessageCount(contactId: $contactId)
  }
`;

interface MyOrder {
  orderId: string;
  status: string;
  amount: number;
  createdAt?: string | null;
  trackingNumber?: string | null;
  carrier?: string | null;
  counterpartyName?: string | null;
  counterpartyEmail?: string | null;
  // The vendor's own company account id — counterpartyEmail above is that
  // company account's own email, not any individual person's, so it can't
  // be used directly to message someone. Use vendorId to look up a real
  // contact via myTradePartnerContacts instead.
  vendorId?: number | null;
  shipmentNo?: string | null;
  estimatedArrivalDate?: string | null;
}

interface MyPayment {
  paymentId: string;
  orderId: string;
  amount: number;
  amountPaid: number;
  status: string;
  paymentMethod?: string | null;
}

interface MyDocument {
  documentId: string;
  orderId: string;
  documentName: string;
  status: string;
  dueDate?: string | null;
  impact?: string | null;
  owner?: string | null;
}

interface MessageThread {
  id: number;
  subject?: string | null;
  relatedOrderId?: number | null;
  lastMessageAt?: string | null;
  unreadCount?: number | null;
}

interface TradePartnerContact {
  id: number;
  name: string;
  email?: string | null;
  partnerId?: number | null;
}

interface CustomerDashboardProps {
  onOrderClick?: (orderId: string) => void;
  onCreateOrder?: () => void;
  onBrowseMarketplace?: () => void;
  onOpenPayments?: (orderId: string) => void;
  onOpenDocuments?: (orderId: string, documentId?: string) => void;
  onOpenMessaging?: (payload: {
    threadId?: number;
    subject?: string;
    orderId?: string;
    targetContactId?: number;
    targetContactName?: string | null;
    targetContactEmail?: string | null;
    draftText?: string;
  }) => void;
  onTrackShipment?: (payload: { orderId: string; shipmentNo?: string | null }) => void;
}

const formatAmount = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(numericValue)) return '—';
  return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const isPaymentSettled = (status: string) => ['paid', 'completed'].includes((status || '').toLowerCase());
const isDocumentActionable = (status: string) => ['pending', 'blocking'].includes((status || '').toLowerCase());
// documents carries two independent signals of "does this block delivery" —
// the status column ('blocking'/'rejected') and the impact column
// ('blocks-delivery') — and a given row can have either set without the
// other. Treat it as blocking if EITHER says so, so this can't silently
// disagree with the Documents page the way impact-only checks did before.
const isDocumentBlocking = (d: MyDocument) =>
  d.impact === 'blocks-delivery' || ['blocking', 'rejected'].includes((d.status || '').toLowerCase());

// Who's actually responsible for a document determines what the customer can
// do about it: something the customer must self-serve, vs. an external party
// (supplier) they can only nudge, vs. Tvikla's own internal team where
// there's no customer-facing action at all — never "Review"/"Message
// supplier" for a document that isn't either the customer's or supplier's.
const OWNER_LABELS: Record<string, string> = {
  customer: 'You',
  supplier: 'Supplier',
  finance: "Tvikla's finance team",
  ops: "Tvikla's ops team",
};
const ownerLabel = (owner?: string | null) => {
  const key = (owner || '').toLowerCase();
  return OWNER_LABELS[key] || (owner ? owner.charAt(0).toUpperCase() + owner.slice(1) : 'Unassigned');
};
const isCustomerOwned = (d: MyDocument) => (d.owner || '').toLowerCase() === 'customer';
const isSupplierOwned = (d: MyDocument) => (d.owner || '').toLowerCase() === 'supplier';

const getDaysInfo = (estimatedArrivalDate?: string | null, status?: string) => {
  if (!estimatedArrivalDate) return null;
  if ((status || '').toLowerCase() === 'delivered') {
    return { label: `Delivered ${estimatedArrivalDate}`, overdue: false };
  }
  const eta = new Date(estimatedArrivalDate);
  if (Number.isNaN(eta.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((eta.getTime() - today.getTime()) / 86400000);
  if (diffDays > 0) return { label: `${diffDays} day${diffDays === 1 ? '' : 's'} to go`, overdue: false };
  if (diffDays < 0) return { label: `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} overdue`, overdue: true };
  return { label: 'Due today', overdue: false };
};

const STAGES = ['Booked', 'Produced', 'In Transit', 'Customs', 'Delivered'];
const STATUS_TO_STAGE: Record<string, number> = {
  Booked: 0,
  'Picked Up': 1,
  'In Transit': 2,
  Customs: 3,
  Delivered: 4,
};

interface DistinctOrder {
  orderId: string;
  amount: number;
  legs: MyOrder[];
  isDraft: boolean;
  isDelivered: boolean;
  currentStatus: string;
  currentStageIndex: number;
  primaryLeg: MyOrder;
}

const buildDistinctOrders = (orders: MyOrder[]): DistinctOrder[] => {
  const map = new Map<string, MyOrder[]>();
  orders.forEach((o) => {
    const arr = map.get(o.orderId) ?? [];
    arr.push(o);
    map.set(o.orderId, arr);
  });
  return Array.from(map.entries()).map(([orderId, legs]) => {
    const isDraft = legs.every((l) => !l.carrier && !l.shipmentNo);
    const isDelivered = legs.every((l) => l.status.toLowerCase() === 'delivered');
    const activeLegs = legs.filter((l) => l.status.toLowerCase() !== 'delivered');
    const primaryLeg = activeLegs[0] ?? legs[0];
    const currentStageIndex = isDelivered ? 4 : (STATUS_TO_STAGE[primaryLeg.status] ?? (isDraft ? -1 : 0));
    return {
      orderId,
      amount: legs[0].amount,
      legs,
      isDraft,
      isDelivered,
      currentStatus: isDelivered ? 'Delivered' : primaryLeg.status,
      currentStageIndex,
      primaryLeg,
    };
  });
};

type Urgency = 'blocking' | 'warning' | 'info';
const URGENCY_RANK: Record<Urgency, number> = { blocking: 0, warning: 1, info: 2 };
const URGENCY_COLOR: Record<Urgency, string> = { blocking: colors.error, warning: colors.primary, info: colors.gray300 };

interface NeedItem {
  key: string;
  urgency: Urgency;
  title: string;
  subtitle: string;
  // Absent when nobody on the customer's side has anything to do — e.g. a
  // document owned by Tvikla's own internal team. Showing "Review" on
  // something the customer can't actually act on is worse than no button.
  actionLabel?: string;
  onAction?: () => void;
  tags?: { label: string; blocking?: boolean }[];
  secondaryAction?: { label: string; onAction: () => void };
  ownerNote?: string;
}

// Color is reserved for meaning, not decoration: neutral is "just a fact",
// 'alert' is "blocking, needs action now", 'attention' is "worth a glance,
// not a problem" — every other KPI stays neutral so these two actually stand out.
type KpiTone = 'default' | 'alert' | 'attention';
const KPI_TONE_COLORS: Record<KpiTone, { border: string; text: string }> = {
  default: { border: colors.gray200, text: '#1c1f26' },
  alert: { border: colors.error, text: colors.error },
  attention: { border: colors.warning, text: '#7a5c00' },
};

const KpiCard: React.FC<{ label: string; value: string; tone?: KpiTone; caption?: string; onClick?: () => void }> = ({ label, value, tone = 'default', caption, onClick }) => {
  const { border, text } = KPI_TONE_COLORS[tone];
  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      style={{
        flex: '1 1 150px',
        minWidth: 150,
        background: '#fff',
        border: `1px solid ${border}`,
        borderRadius: 12,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: tone === 'default' ? colors.gray500 : text }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: onClick && tone === 'default' ? colors.primary : text }}>{value}</div>
      {caption && <div style={{ fontSize: 11, color: colors.gray400 }}>{caption}</div>}
    </div>
  );
};

const ProgressBar: React.FC<{ currentIndex: number; delivered: boolean }> = ({ currentIndex, delivered }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
    {STAGES.map((stage, i) => {
      const done = delivered || i < currentIndex;
      const current = !delivered && i === currentIndex;
      const color = done ? colors.success : current ? colors.primary : colors.gray200;
      return (
        <div key={stage} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ height: 8, borderRadius: 4, background: color, opacity: current ? 0.55 : 1 }} />
          <div style={{ fontSize: 12, fontWeight: 600, color: done || current ? '#1c1f26' : colors.gray400 }}>{stage}</div>
        </div>
      );
    })}
  </div>
);

const Chip: React.FC<{ label: string; dotColor: string; onClick?: () => void }> = ({ label, dotColor, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 7,
      border: `1px solid ${colors.gray200}`,
      borderRadius: 20,
      padding: '6px 12px',
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, display: 'inline-block' }} />
    <span style={{ fontSize: 12, fontWeight: 600, color: '#1c1f26' }}>{label}</span>
  </div>
);

export const CustomerDashboard: React.FC<CustomerDashboardProps> = ({
  onOrderClick,
  onBrowseMarketplace,
  onOpenPayments,
  onOpenDocuments,
  onOpenMessaging,
  onTrackShipment,
}) => {
  const { user } = useUser();
  const customerId = user?.customerId;
  const contactId = user?.contactId ?? 0;
  const hasIdentity = customerId !== undefined;
  type OrderFilter = 'all' | 'transit' | 'delivered' | 'draft' | 'shipping' | 'arrivingSoon' | 'blocked' | 'paymentsDue';
  const [filter, setFilter] = useState<OrderFilter>('all');
  const orderListRef = useRef<HTMLDivElement>(null);

  const { data, loading } = useGraphQLQuery<{
    myOrders: MyOrder[];
    myPayments: MyPayment[];
    myDocuments: MyDocument[];
    myTradePartnerContacts: { items: TradePartnerContact[] };
    messageThreads: MessageThread[];
    unreadMessageCount: number;
  }>(CUSTOMER_DASHBOARD_QUERY, {
    variables: { customerId: customerId ?? null, contactId },
    skip: !hasIdentity,
  });

  const orders = data?.myOrders ?? [];
  const payments = data?.myPayments ?? [];
  const documents = data?.myDocuments ?? [];
  const threads = data?.messageThreads ?? [];
  const tradePartnerContacts = data?.myTradePartnerContacts?.items ?? [];

  // A vendor can have more than one Customer-visible contact — pick the
  // first, there's no "primary" concept on the trade-partner side to prefer.
  const supplierContactByVendorId = new Map<number, TradePartnerContact>();
  tradePartnerContacts.forEach((c) => {
    if (c.partnerId != null && !supplierContactByVendorId.has(c.partnerId)) {
      supplierContactByVendorId.set(c.partnerId, c);
    }
  });
  const resolveSupplierContact = (vendorId?: number | null) =>
    vendorId != null ? supplierContactByVendorId.get(vendorId) : undefined;

  const pendingPayments = payments.filter((p) => !isPaymentSettled(p.status));
  const actionableDocuments = documents.filter((d) => isDocumentActionable(d.status));
  const unreadThreads = threads.filter((t) => (t.unreadCount ?? 0) > 0);

  const distinctOrders = buildDistinctOrders(orders);
  const openValue = distinctOrders.filter((o) => !o.isDraft && !o.isDelivered).reduce((sum, o) => sum + o.amount, 0);
  const dueSoonAmount = pendingPayments.reduce((sum, p) => sum + Math.max(p.amount - p.amountPaid, 0), 0);
  const inProgressCount = distinctOrders.filter((o) => !o.isDraft && !o.isDelivered).length;
  const deliveredCount = distinctOrders.filter((o) => o.isDelivered).length;
  const draftCount = distinctOrders.filter((o) => o.isDraft).length;
  const blockedOrderIds = new Set(actionableDocuments.filter((d) => isDocumentBlocking(d)).map((d) => d.orderId));

  // Literal "In Transit" stage only — narrower than inProgressCount, which
  // also includes Booked/Produced/Customs.
  const inTransitStageIndex = STAGES.indexOf('In Transit');
  const inTransitCount = distinctOrders.filter(
    (o) => !o.isDraft && !o.isDelivered && o.currentStageIndex === inTransitStageIndex
  ).length;

  // Orders with an ETA in the next week — the ones a customer should
  // actually be preparing for (customs clearance, receiving), as opposed to
  // everything still somewhere in the pipeline. Factored out so the KPI
  // count and the drill-down filter below always agree on the definition.
  const APPROACHING_SOON_DAYS = 7;
  const isApproachingSoon = (o: DistinctOrder) => {
    if (o.isDraft || o.isDelivered) return false;
    const eta = o.primaryLeg.estimatedArrivalDate;
    if (!eta) return false;
    const etaDate = new Date(eta);
    if (Number.isNaN(etaDate.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((etaDate.getTime() - today.getTime()) / 86400000);
    return diffDays >= 0 && diffDays <= APPROACHING_SOON_DAYS;
  };
  const approachingSoonCount = distinctOrders.filter(isApproachingSoon).length;
  const pendingPaymentOrderIds = new Set(pendingPayments.map((p) => p.orderId));

  const documentsByOrder = new Map<string, MyDocument[]>();
  actionableDocuments.forEach((d) => {
    const arr = documentsByOrder.get(d.orderId) ?? [];
    arr.push(d);
    documentsByOrder.set(d.orderId, arr);
  });

  const documentNeedItems: NeedItem[] = Array.from(documentsByOrder.entries()).map(([orderId, docs]) => {
    const blockingDoc = docs.find((d) => isDocumentBlocking(d));
    const isBlocking = Boolean(blockingDoc);
    const order = distinctOrders.find((o) => o.orderId === orderId);
    // NOT order.primaryLeg.counterpartyEmail — that's the vendor's own
    // company account email (vendors.email), not a real person's contact,
    // and messaging matches against the contacts table. Using it directly
    // was silently failing to find any match and falling into a broken
    // fallback that opened an unrelated, arbitrary existing thread.
    const supplierContact = resolveSupplierContact(order?.primaryLeg.vendorId);
    const customerDocs = docs.filter(isCustomerOwned);
    const supplierDocs = docs.filter(isSupplierOwned);
    const internalDocs = docs.filter((d) => !isCustomerOwned(d) && !isSupplierOwned(d));

    if (docs.length === 1) {
      const d = docs[0];
      let actionLabel: string | undefined;
      let onAction: (() => void) | undefined;
      let secondaryAction: NeedItem['secondaryAction'];
      let ownerNote: string | undefined;
      let title: string;

      if (isCustomerOwned(d)) {
        title = `${d.documentName} needs your review`;
        actionLabel = 'Review';
        onAction = () => onOpenDocuments?.(d.orderId, d.documentId);
      } else if (isSupplierOwned(d)) {
        title = `${d.documentName} needs supplier action`;
        if (supplierContact) {
          actionLabel = 'Message supplier';
          onAction = () => onOpenMessaging?.({
            orderId: d.orderId,
            targetContactId: supplierContact.id,
            targetContactName: supplierContact.name,
            targetContactEmail: supplierContact.email ?? undefined,
            draftText: `Hi ${supplierContact.name} — could we sort out "${d.documentName}" on ${d.orderId}? It's currently blocking delivery.`,
          });
        } else {
          ownerNote = 'No supplier contact on file yet for this order.';
        }
        secondaryAction = { label: 'View status', onAction: () => onOpenDocuments?.(d.orderId, d.documentId) };
      } else {
        title = `${d.documentName} pending with ${ownerLabel(d.owner)}`;
        ownerNote = `Being handled by ${ownerLabel(d.owner)} — no action needed from you.`;
        secondaryAction = { label: 'View status', onAction: () => onOpenDocuments?.(d.orderId, d.documentId) };
      }

      return {
        key: `doc-${d.documentId}`,
        urgency: (isBlocking ? 'blocking' : 'warning') as Urgency,
        title,
        subtitle: `${isBlocking ? 'Blocks delivery · ' : ''}${d.orderId}${d.dueDate ? ` · due ${d.dueDate}` : ''}`,
        actionLabel,
        onAction,
        secondaryAction,
        ownerNote,
      };
    }

    const earliestDue = docs.map((d) => d.dueDate).filter(Boolean).sort()[0];
    let actionLabel: string | undefined;
    let onAction: (() => void) | undefined;
    let secondaryAction: NeedItem['secondaryAction'];
    let ownerNote: string | undefined;
    const internalNote = internalDocs.length > 0
      ? `${internalDocs.length} of these ${internalDocs.length === 1 ? 'is' : 'are'} being handled by Tvikla's team — no action needed from you on ${internalDocs.length === 1 ? 'that one' : 'those'}.`
      : undefined;

    if (customerDocs.length > 0) {
      actionLabel = 'Review all';
      onAction = () => onOpenDocuments?.(orderId, (blockingDoc ?? customerDocs[0]).documentId);
      if (supplierDocs.length > 0 && supplierContact) {
        secondaryAction = {
          label: 'Message supplier',
          onAction: () => onOpenMessaging?.({
            orderId,
            targetContactId: supplierContact.id,
            targetContactName: supplierContact.name,
            targetContactEmail: supplierContact.email ?? undefined,
            draftText: `Hi ${supplierContact.name} — could you help resolve ${supplierDocs.map((d) => d.documentName).join(', ')} on ${orderId}?`,
          }),
        };
      }
      ownerNote = internalNote;
    } else if (supplierDocs.length > 0) {
      if (supplierContact) {
        actionLabel = 'Message supplier';
        onAction = () => onOpenMessaging?.({
          orderId,
          targetContactId: supplierContact.id,
          targetContactName: supplierContact.name,
          targetContactEmail: supplierContact.email ?? undefined,
          draftText: `Hi ${supplierContact.name} — a few documents need attention on ${orderId} (${supplierDocs.map((d) => d.documentName).join(', ')}). Could we sync to resolve this quickly?`,
        });
        ownerNote = internalNote;
      } else {
        ownerNote = internalNote ? `No supplier contact on file yet. ${internalNote}` : 'No supplier contact on file yet for this order.';
      }
      secondaryAction = { label: 'View status', onAction: () => onOpenDocuments?.(orderId, (blockingDoc ?? docs[0]).documentId) };
    } else {
      secondaryAction = { label: 'View status', onAction: () => onOpenDocuments?.(orderId, (blockingDoc ?? docs[0]).documentId) };
      ownerNote = "Being handled by Tvikla's team — no action needed from you.";
    }

    return {
      key: `docs-${orderId}`,
      urgency: (isBlocking ? 'blocking' : 'warning') as Urgency,
      title: `${orderId} requires ${docs.length} compliance updates`,
      subtitle: `${isBlocking ? 'Blocks delivery · ' : ''}${orderId}${earliestDue ? ` · due ${earliestDue}` : ''}`,
      actionLabel,
      onAction,
      tags: docs.map((d) => ({ label: `${d.documentName} · ${ownerLabel(d.owner)}`, blocking: isDocumentBlocking(d) })),
      secondaryAction,
      ownerNote,
    };
  });

  const needsYouNow: NeedItem[] = [
    ...documentNeedItems,
    ...pendingPayments.map((p) => {
      const order = distinctOrders.find((o) => o.orderId === p.orderId);
      const daysInfo = getDaysInfo(order?.primaryLeg.estimatedArrivalDate, order?.primaryLeg.status);
      const owed = Math.max(p.amount - p.amountPaid, 0);
      return {
        key: `pay-${p.paymentId}`,
        urgency: 'warning' as Urgency,
        title: `${formatAmount(owed)} payment due${order?.primaryLeg.counterpartyName ? ` to ${order.primaryLeg.counterpartyName}` : ''}`,
        subtitle: `${p.orderId}${order?.primaryLeg.estimatedArrivalDate ? ` · due ${order.primaryLeg.estimatedArrivalDate}` : ''}${daysInfo ? ` · ${daysInfo.label}` : ''}`,
        actionLabel: 'Pay now',
        onAction: () => onOpenPayments?.(p.orderId),
      };
    }),
    ...unreadThreads.map((t) => ({
      key: `note-${t.id}`,
      urgency: 'info' as Urgency,
      title: `${t.subject ?? 'New message'}`,
      subtitle: `Unread${t.relatedOrderId ? ` · Order #${t.relatedOrderId}` : ''}${t.lastMessageAt ? ` · ${String(t.lastMessageAt).slice(0, 10)}` : ''}`,
      actionLabel: 'Open thread',
      onAction: () => onOpenMessaging?.({ threadId: t.id, subject: t.subject ?? undefined }),
    })),
  ].sort((a, b) => URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency]);

  const filteredOrders = distinctOrders.filter((o) => {
    if (filter === 'transit') return !o.isDraft && !o.isDelivered;
    if (filter === 'delivered') return o.isDelivered;
    if (filter === 'draft') return o.isDraft;
    if (filter === 'shipping') return !o.isDraft && !o.isDelivered && o.currentStageIndex === inTransitStageIndex;
    if (filter === 'arrivingSoon') return isApproachingSoon(o);
    if (filter === 'blocked') return blockedOrderIds.has(o.orderId);
    if (filter === 'paymentsDue') return pendingPaymentOrderIds.has(o.orderId);
    return true;
  });

  const applyFilterAndScroll = (value: OrderFilter) => {
    setFilter(value);
    orderListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = (user?.name || 'there').split(' ')[0];
  // Deliberately counts action items (a document review, a payment, an
  // unread message), not orders — one order can need attention on more than
  // one of those at once, so this won't always match an order-count KPI.
  const subtitle = needsYouNow.length > 0
    ? `${needsYouNow.length} action item${needsYouNow.length === 1 ? '' : 's'} need${needsYouNow.length === 1 ? 's' : ''} you today. Everything else is on track.`
    : "Nothing needs you today — everything's on track.";

  const chipStyle = (active: boolean): React.CSSProperties => ({
    background: active ? '#1c1f26' : '#fff',
    color: active ? '#fff' : '#3f444d',
    fontSize: 12,
    fontWeight: active ? 700 : 600,
    padding: '7px 13px',
    borderRadius: 20,
    border: active ? 'none' : `1px solid ${colors.gray200}`,
    cursor: 'pointer',
  });

  if (!hasIdentity) {
    return (
      <DashboardContainer>
        <div style={{ padding: 24, color: colors.gray500 }}>
          This login isn't linked to a real customer record yet, so there's nothing to show.
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <H1 style={{ margin: 0 }}>{greeting}, {firstName}</H1>
            <div style={{ fontSize: 14, color: colors.gray500 }}>{loading ? 'Loading your dashboard…' : subtitle}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: colors.gray500, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: colors.success, display: 'inline-block' }} />
              Live
            </div>
            <Button variant="outline" onClick={onBrowseMarketplace}>Browse Marketplace</Button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap' }}>
          <KpiCard label="Open value" value={formatAmount(openValue)} caption="In-process orders only" onClick={() => applyFilterAndScroll('transit')} />
          <KpiCard label="Payments due" value={formatAmount(dueSoonAmount)} caption="Across all orders, delivered too" onClick={() => applyFilterAndScroll('paymentsDue')} />
          <KpiCard label="In progress" value={String(inProgressCount)} caption="Booked through Customs" onClick={() => applyFilterAndScroll('transit')} />
          <KpiCard label="In transit" value={String(inTransitCount)} caption="Currently shipping" onClick={() => applyFilterAndScroll('shipping')} />
          <KpiCard
            label="Arriving soon"
            value={String(approachingSoonCount)}
            caption={`ETA within ${APPROACHING_SOON_DAYS} days`}
            tone={approachingSoonCount > 0 ? 'attention' : 'default'}
            onClick={() => applyFilterAndScroll('arrivingSoon')}
          />
          <KpiCard
            label="Blocked"
            value={blockedOrderIds.size > 0 ? `${blockedOrderIds.size} order${blockedOrderIds.size === 1 ? '' : 's'}` : 'None'}
            tone={blockedOrderIds.size > 0 ? 'alert' : 'default'}
            onClick={() => applyFilterAndScroll('blocked')}
          />
        </div>

        <div style={{ background: '#fff', border: `1px solid ${colors.gray200}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: `1px solid ${colors.gray100}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#1c1f26' }}>Needs you now</div>
              {needsYouNow.length > 0 && (
                <div style={{ background: '#fdecea', color: colors.error, fontSize: 12, fontWeight: 800, padding: '3px 9px', borderRadius: 20 }}>
                  {needsYouNow.length}
                </div>
              )}
            </div>
            <div style={{ fontSize: 13, color: colors.gray500 }}>Sorted by what stops a shipment first</div>
          </div>

          {needsYouNow.length === 0 && !loading && (
            <div style={{ padding: '22px', display: 'flex', alignItems: 'center', gap: 8, color: colors.gray500 }}>
              <Icon name="CheckCircle" color={colors.success} />
              Nothing needs your attention right now.
            </div>
          )}

          {needsYouNow.map((item, index) => (
            <div
              key={item.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 18,
                padding: '18px 22px',
                borderBottom: index === needsYouNow.length - 1 ? 'none' : `1px solid ${colors.gray100}`,
                borderLeft: `3px solid ${URGENCY_COLOR[item.urgency]}`,
              }}
            >
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ fontSize: 15, fontWeight: item.urgency === 'blocking' ? 700 : 500, color: '#1c1f26' }}>{item.title}</div>
                <div style={{ fontSize: 12, color: colors.gray400 }}>{item.subtitle}</div>
                {item.ownerNote && (
                  <div style={{ fontSize: 12, color: colors.gray500, fontStyle: 'italic' }}>{item.ownerNote}</div>
                )}
                {item.tags && item.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                    {item.tags.map((tag) => (
                      <span
                        key={tag.label}
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: tag.blocking ? colors.error : colors.gray600,
                          background: tag.blocking ? '#fdecea' : colors.gray100,
                          borderRadius: 6,
                          padding: '2px 7px',
                        }}
                      >
                        {tag.blocking ? '⚠ ' : ''}{tag.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {item.secondaryAction && (
                  <Button variant="outline" onClick={item.secondaryAction.onAction}>
                    {item.secondaryAction.label}
                  </Button>
                )}
                {item.actionLabel && item.onAction && (
                <Button
                  variant={item.urgency === 'blocking' ? undefined : 'outline'}
                  style={item.urgency === 'blocking' ? { background: URGENCY_COLOR.blocking, borderColor: URGENCY_COLOR.blocking } : undefined}
                  onClick={item.onAction}
                >
                  {item.actionLabel}
                </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div ref={orderListRef} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={chipStyle(filter === 'all')} onClick={() => setFilter('all')}>All orders {distinctOrders.length}</button>
          <button style={chipStyle(filter === 'transit')} onClick={() => setFilter('transit')}>In progress {inProgressCount}</button>
          <button style={chipStyle(filter === 'shipping')} onClick={() => setFilter('shipping')}>In transit {inTransitCount}</button>
          <button style={chipStyle(filter === 'arrivingSoon')} onClick={() => setFilter('arrivingSoon')}>Arriving soon {approachingSoonCount}</button>
          <button style={chipStyle(filter === 'blocked')} onClick={() => setFilter('blocked')}>Blocked {blockedOrderIds.size}</button>
          <button style={chipStyle(filter === 'paymentsDue')} onClick={() => setFilter('paymentsDue')}>Payments due {pendingPaymentOrderIds.size}</button>
          <button style={chipStyle(filter === 'delivered')} onClick={() => setFilter('delivered')}>Delivered {deliveredCount}</button>
          <button style={chipStyle(filter === 'draft')} onClick={() => setFilter('draft')}>Drafts {draftCount}</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filteredOrders.length === 0 && !loading && (
            <div style={{ background: '#fff', border: `1px solid ${colors.gray200}`, borderRadius: 14, padding: 22, color: colors.gray500 }}>
              No orders match this filter.
            </div>
          )}

          {filteredOrders.map((o) => {
            const orderDocs = actionableDocuments.filter((d) => d.orderId === o.orderId);
            const orderPayments = pendingPayments.filter((p) => p.orderId === o.orderId);
            const daysInfo = getDaysInfo(o.primaryLeg.estimatedArrivalDate, o.currentStatus);
            const blockerCount = orderDocs.filter((d) => isDocumentBlocking(d)).length;

            if (o.isDraft) {
              return (
                <div
                  key={o.orderId}
                  style={{
                    background: '#fff',
                    border: `1px dashed ${colors.gray300}`,
                    borderRadius: 14,
                    padding: '20px 22px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 24,
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: 220, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <div style={{ fontSize: 17, fontWeight: 700, color: colors.gray500 }}>{o.orderId}</div>
                      <div style={{ background: colors.gray100, color: colors.gray500, fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Draft
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: colors.gray400 }}>No supplier selected</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: colors.gray500 }}>Pick a supplier and submit to start the lifecycle.</div>
                  <Button variant="outline" onClick={() => onOrderClick?.(o.orderId)}>Continue draft</Button>
                  <div style={{ fontSize: 17, fontWeight: 700, color: colors.gray500 }}>{formatAmount(o.amount)}</div>
                </div>
              );
            }

            if (o.isDelivered) {
              return (
                <div
                  key={o.orderId}
                  style={{
                    background: '#fff',
                    border: `1px solid ${colors.gray200}`,
                    borderRadius: 14,
                    padding: '20px 22px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 24,
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: 220, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <div
                        style={{ fontSize: 17, fontWeight: 700, color: '#1c1f26', cursor: 'pointer' }}
                        onClick={() => onOrderClick?.(o.orderId)}
                      >
                        {o.orderId}
                      </div>
                      <div style={{ background: '#e8f6ec', color: colors.success, fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Delivered
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: colors.gray500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.primaryLeg.counterpartyName ?? '—'}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 4 }}>
                    {STAGES.map((s) => (
                      <div key={s} style={{ flex: 1, height: 8, borderRadius: 4, background: colors.success }} />
                    ))}
                  </div>
                  <div style={{ fontSize: 13, color: colors.gray500, width: 160, flexShrink: 0 }}>
                    {daysInfo ? daysInfo.label : `Delivered ${o.primaryLeg.estimatedArrivalDate ?? ''}`}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: '#1c1f26' }}>{formatAmount(o.amount)}</div>
                </div>
              );
            }

            return (
              <div key={o.orderId} style={{ background: '#fff', border: `1px solid ${colors.gray200}`, borderRadius: 14, padding: 22, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#1c1f26', cursor: 'pointer' }} onClick={() => onOrderClick?.(o.orderId)}>
                        {o.orderId}
                      </div>
                      <div style={{ background: '#fdefe7', color: colors.primary, fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {o.currentStatus}
                      </div>
                      {blockerCount > 0 && (
                        <div style={{ background: '#fdecea', color: colors.error, fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 20 }}>
                          {blockerCount} blocker{blockerCount === 1 ? '' : 's'}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: colors.gray500, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {[o.primaryLeg.counterpartyName, o.primaryLeg.estimatedArrivalDate ? `ETA ${o.primaryLeg.estimatedArrivalDate}` : null]
                        .filter(Boolean)
                        .join(' · ') || 'Awaiting shipment details'}
                      {daysInfo && (
                        <span style={{ color: daysInfo.overdue ? colors.error : colors.gray500, fontWeight: daysInfo.overdue ? 700 : 400 }}>
                          ({daysInfo.label})
                        </span>
                      )}
                      {o.primaryLeg.carrier && (
                        <>
                          <span>·</span>
                          <Button
                            variant="link"
                            style={{ padding: 0, fontSize: 13 }}
                            onClick={() => onTrackShipment?.({ orderId: o.orderId, shipmentNo: o.primaryLeg.shipmentNo })}
                          >
                            {o.primaryLeg.carrier}{o.primaryLeg.trackingNumber ? ` · ${o.primaryLeg.trackingNumber}` : ''}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#1c1f26' }}>{formatAmount(o.amount)}</div>
                    {orderPayments.length > 0 && (
                      <div style={{ fontSize: 12, color: daysInfo?.overdue ? colors.error : colors.gray500 }}>
                        Payment {daysInfo?.overdue ? 'overdue' : 'due'} {o.primaryLeg.estimatedArrivalDate ?? ''}
                      </div>
                    )}
                  </div>
                </div>

                <ProgressBar currentIndex={o.currentStageIndex} delivered={false} />

                {(orderDocs.length > 0 || orderPayments.length > 0) && (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', paddingTop: 4, borderTop: `1px solid ${colors.gray100}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: colors.gray400, textTransform: 'uppercase', letterSpacing: '0.08em', paddingTop: 12 }}>
                      Open items
                    </div>
                    {orderDocs.map((d) => (
                      <div style={{ marginTop: 12 }} key={d.documentId}>
                        <Chip
                          label={d.documentName}
                          dotColor={isDocumentBlocking(d) ? colors.error : colors.primary}
                          onClick={() => onOpenDocuments?.(d.orderId, d.documentId)}
                        />
                      </div>
                    ))}
                    {orderPayments.map((p) => (
                      <div style={{ marginTop: 12 }} key={p.paymentId}>
                        <Chip label={`${formatAmount(Math.max(p.amount - p.amountPaid, 0))} due`} dotColor={colors.primary} />
                      </div>
                    ))}
                    <div style={{ marginTop: 12, marginLeft: 'auto' }}>
                      <Button variant="outline" onClick={() => onOrderClick?.(o.orderId)}>Open order</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </DashboardContainer>
  );
};
